import {
  tipoImovelTextRegistry,
  usoLocacaoTextRegistry,
  clausulaGarantiaTextRegistry,
  conjuge,
} from "../dynamicTextRegistry";
import { maskCpf, maskPhoneBr } from "../../utils/masks";
import { formatBrlCurrencyForDoc, formatDecimalBrForDoc } from "../../utils/brFormat";

function formatCpfPreview(value) {
  return maskCpf(value ?? "");
}

function formatPhonePreview(value) {
  return maskPhoneBr(value ?? "");
}

function formatDateBR(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return s;
  return parsed.toLocaleDateString("pt-BR");
}

function escapeHtml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function strongLabel(text) {
  return `<strong>${escapeHtml(text)}</strong>`;
}

function replacePlaceholdersInStringEscaped(template, values) {
  if (!template) return "";
  return template.replace(/\{(.*?)\}/g, (_, key) => escapeHtml(values[key] ?? ""));
}

function boldConjugeTemplateLabels(html) {
  return html
    .replace(/CÔNJUGE:/g, `${strongLabel("CÔNJUGE:")}`)
    .replace(/\bCPF:/g, `${strongLabel("CPF:")}`)
    .replace(/\bRG:/g, `${strongLabel("RG:")}`)
    .replace(/NACIONALIDADE:/g, `${strongLabel("NACIONALIDADE:")}`)
    .replace(/PROFISSÃO:/g, `${strongLabel("PROFISSÃO:")}`)
    .replace(/\bEMAIL:/g, `${strongLabel("EMAIL:")}`);
}

function renderConjugeBlockHtml(person) {
  if (person?.ESTADO_CIVIL_PESSOA_FISICA !== "casado") return "";
  const tpl = conjuge.casado;
  if (!tpl) return "";
  const valuesForPreview = {
    ...person,
    CPF_CONJUGE: formatCpfPreview(person.CPF_CONJUGE),
  };
  const filled = replacePlaceholdersInStringEscaped(tpl, valuesForPreview).trim();
  if (!filled) return "";
  return boldConjugeTemplateLabels(filled);
}

function buildPessoaFisicaBlockHtml(person) {
  const parts = [
    `${strongLabel("NOME:")} ${escapeHtml(person.NOME_PESSOA_FISICA)}`,
    `${strongLabel("CPF:")} ${escapeHtml(formatCpfPreview(person.CPF_PESSOA_FISICA))}`,
    `${strongLabel("RG:")} ${escapeHtml(person.RG_PESSOA_FISICA)}`,
    `${strongLabel("NACIONALIDADE:")} ${escapeHtml(person.NACIONALIDADE_PESSOA_FISICA)}`,
    `${strongLabel("PROFISSAO:")} ${escapeHtml(person.PROFISSAO_PESSOA_FISICA)}`,
    `${strongLabel("ESTADO CIVIL:")} ${escapeHtml(person.ESTADO_CIVIL_PESSOA_FISICA)}`,
  ];
  const conj = renderConjugeBlockHtml(person);
  if (conj) parts.push(conj);
  parts.push(
    `${strongLabel("ENDERECO:")} ${escapeHtml(person.RUA_ENDERECO_PESSOA_FISICA)}, ${strongLabel("N:")} ${escapeHtml(person.N_ENDERECO_PESSOA_FISICA)}, ${strongLabel("BAIRRO:")} ${escapeHtml(person.BAIRRO_ENDERECO_PESSOA_FISICA)}, ${escapeHtml(person.MUN_ENDERECO_PESSOA_FISICA)} ${strongLabel("- CEP:")} ${escapeHtml(person.CEP_PESSOA_FISICA)}`,
    `${strongLabel("EMAIL:")} ${escapeHtml(person.EMAIL_PESSOA_FISICA)}`,
    `${strongLabel("TELEFONE:")} ${escapeHtml(formatPhonePreview(person.TEL_PESSOA_FISICA))}`
  );
  return parts.join(" ");
}

function signatureBlock(name, cpf) {
  return `________________________\n${name ?? ""}\n${formatCpfPreview(cpf)}`;
}

function signatureBlockConjugeHtml(name, cpf) {
  const nameEsc = escapeHtml(name ?? "");
  const cpfEsc = escapeHtml(formatCpfPreview(cpf));
  return `<div class="signature-conjuge-block space-y-1 mb-1"><div>________________________</div><div>${nameEsc}</div><div>${cpfEsc}</div></div>`;
}

export function contratoLocacaoImovelResolver(formData, userConfig) {
  const safeUserConfig = userConfig || {};
  const data = { ...safeUserConfig, ...formData };

  data.ENDERECO_IMOBILIARIA =
    formData.ENDERECO_IMOBILIARIA !== undefined &&
    formData.ENDERECO_IMOBILIARIA !== null
      ? formData.ENDERECO_IMOBILIARIA
      : safeUserConfig.endereco_imobiliaria || "";
  data.CELULAR_IMOBILIARIA = formatPhonePreview(
    formData.CELULAR_IMOBILIARIA !== undefined &&
      formData.CELULAR_IMOBILIARIA !== null
      ? formData.CELULAR_IMOBILIARIA
      : safeUserConfig.celular_imobiliaria || ""
  );
  data.EMAIL_IMOBILIARIA =
    formData.EMAIL_IMOBILIARIA !== undefined &&
    formData.EMAIL_IMOBILIARIA !== null
      ? formData.EMAIL_IMOBILIARIA
      : safeUserConfig.email_imobiliaria || "";
  data.CRECI_IMOBILIARIA =
    formData.CRECI_IMOBILIARIA !== undefined &&
    formData.CRECI_IMOBILIARIA !== null
      ? formData.CRECI_IMOBILIARIA
      : safeUserConfig.creci_imobiliaria || "";
  data.SITE_IMOBILIARIA =
    formData.SITE_IMOBILIARIA !== undefined &&
    formData.SITE_IMOBILIARIA !== null
      ? formData.SITE_IMOBILIARIA
      : safeUserConfig.site_imobiliaria || "";

  const fiadores = Array.isArray(formData.fiadores) ? formData.fiadores : [];
  const usesFiadorClause =
    String(formData.clausula_garantia?.type ?? "")
      .toLowerCase()
      === "fiador";

  if (formData.locadores?.length) {
    data.LOCADORES = formData.locadores.map(buildPessoaFisicaBlockHtml).join("<br /><br />");
    data.ASSINATURA_LOCADOR = formData.locadores
      .map((v) => signatureBlock(v.NOME_PESSOA_FISICA, v.CPF_PESSOA_FISICA))
      .join("\n\n");
    data.ASSINATURA_CONJUGE_LOCADOR = formData.locadores
      .filter((v) => v.ESTADO_CIVIL_PESSOA_FISICA === "casado")
      .map((v) => signatureBlockConjugeHtml(v.NOME_CONJUGE, v.CPF_CONJUGE))
      .join('<div class="h-4" aria-hidden="true"></div>');
  }

  if (formData.locatarios?.length) {
    data.LOCATARIOS = formData.locatarios.map(buildPessoaFisicaBlockHtml).join("<br /><br />");
    data.ASSINATURA_LOCATARIO = formData.locatarios
      .map((v) => signatureBlock(v.NOME_PESSOA_FISICA, v.CPF_PESSOA_FISICA))
      .join("\n\n");
    data.ASSINATURA_CONJUGE_LOCATARIO = formData.locatarios
      .filter((v) => v.ESTADO_CIVIL_PESSOA_FISICA === "casado")
      .map((v) => signatureBlockConjugeHtml(v.NOME_CONJUGE, v.CPF_CONJUGE))
      .join('<div class="h-4" aria-hidden="true"></div>');
  }

  if (usesFiadorClause && fiadores.length) {
    data.FIADORES = fiadores.map(buildPessoaFisicaBlockHtml).join("<br /><br />");
    data.ASSINATURA_FIADOR = fiadores
      .map((v) => signatureBlock(v.NOME_PESSOA_FISICA, v.CPF_PESSOA_FISICA))
      .join("\n\n");
    data.ASSINATURA_CONJUGE_FIADOR = fiadores
      .filter((v) => v.ESTADO_CIVIL_PESSOA_FISICA === "casado")
      .map((v) => signatureBlockConjugeHtml(v.NOME_CONJUGE, v.CPF_CONJUGE))
      .join('<div class="h-4" aria-hidden="true"></div>');
  } else {
    data.FIADORES = escapeHtml("Sem Fiadores");
    data.ASSINATURA_FIADOR = "";
    data.ASSINATURA_CONJUGE_FIADOR = "";
  }

  if (formData.testemunhas?.length) {
    data.ASSINATURA_TESTEMUNHA = formData.testemunhas
      .map((t) => signatureBlock(t.NOME_TESTEMUNHA, t.CPF_TESTEMUNHA))
      .join("\n\n");
  }

  if (formData.tipo_imovel?.type) {
    const templateText = tipoImovelTextRegistry[formData.tipo_imovel.type];
    data.TIPO_IMOVEL = templateText?.replace(
      /\{(.*?)\}/g,
      (_, key) => formData.tipo_imovel.fields?.[key] || ""
    );
  }

  if (formData.USO_LOCACAO) {
    data.USO_LOCACAO =
      usoLocacaoTextRegistry[formData.USO_LOCACAO] ?? formData.USO_LOCACAO;
  }

  if (formData.clausula_garantia?.type) {
    const templateText = clausulaGarantiaTextRegistry[formData.clausula_garantia.type];
    data.CLAUSULA_GARANTIA = templateText?.replace(/\{(.*?)\}/g, (_, key) => {
      const raw = formData.clausula_garantia.fields?.[key] ?? "";
      if (key === "VALOR_CAUCAO") return formatBrlCurrencyForDoc(raw);
      if (key === "MESES_CAUCAO") return formatDecimalBrForDoc(raw);
      return raw;
    }) || "";
  }

  if (data.DATA_ASSINATURA) {
    data.DATA_ASSINATURA = formatDateBR(data.DATA_ASSINATURA);
  }

  if (data.VALOR_ALUGUEL != null) {
    data.VALOR_ALUGUEL = formatBrlCurrencyForDoc(data.VALOR_ALUGUEL);
  }
  if (data.MULTA_RESCISAO_ANTECIPADA != null) {
    data.MULTA_RESCISAO_ANTECIPADA = formatBrlCurrencyForDoc(data.MULTA_RESCISAO_ANTECIPADA);
  }
  if (data.MULTA_ATRASO != null) {
    data.MULTA_ATRASO = formatDecimalBrForDoc(data.MULTA_ATRASO);
  }
  if (data.MULTA_INFRACAO != null) {
    data.MULTA_INFRACAO = formatDecimalBrForDoc(data.MULTA_INFRACAO);
  }

  return data;
}
